import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, User, ArrowLeft } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLang } from '../i18n/LanguageContext';

export default function LoginPage() {
  const { login } = useAuth();
  const { toast } = useToast();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(form.username, form.password);
      toast({ title: t('ext.welcomeToast'), description: `${t('ext.welcomeToastDesc')}, ${data.displayName || data.username}` });
      nav('/extranet');
    } catch (err) {
      toast({ title: t('ext.errorTitle'), description: err?.response?.data?.detail || t('ext.loginError') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Header onCTA={() => nav('/')} />
      <section className="min-h-screen pt-32 pb-16 bg-brand-navy relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-brand-yellow/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-brand-purple/20 blur-3xl" />

        <div className="relative z-10 max-w-md mx-auto px-6">
          <Link to="/" data-testid="login-back-home-link" className="inline-flex items-center gap-2 text-white/70 hover:text-brand-yellow text-sm mb-6">
            <ArrowLeft className="w-4 h-4" /> {t('ext.backHome')}
          </Link>

          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-yellow/15 border border-brand-yellow mb-4">
                <Lock className="w-6 h-6 text-brand-navy" />
              </div>
              <h1 data-testid="login-title" className="text-2xl font-bold text-brand-navy">{t('ext.loginTitle')}</h1>
              <p data-testid="login-subtitle" className="text-sm text-gray-500 mt-2">{t('ext.loginSubtitle')}</p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label data-testid="login-username-label" className="block text-xs font-semibold text-brand-navy uppercase tracking-wider mb-2">{t('ext.username')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input data-testid="login-username-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 rounded border border-gray-200 focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 transition"
                    placeholder={t('ext.usernamePh')} autoComplete="username" />
                </div>
              </div>
              <div>
                <label data-testid="login-password-label" className="block text-xs font-semibold text-brand-navy uppercase tracking-wider mb-2">{t('ext.password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input data-testid="login-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 rounded border border-gray-200 focus:outline-none focus:border-brand-yellow focus:ring-2 focus:ring-brand-yellow/30 transition"
                    placeholder={t('ext.passwordPh')} autoComplete="current-password" />
                </div>
              </div>
              <button data-testid="login-submit-button" type="submit" disabled={busy} className="btn-yellow w-full justify-center disabled:opacity-60">
                {busy ? t('ext.signingIn') : t('ext.signIn')}
              </button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-6">
              {t('ext.noAccess')} <a href="mailto:denis@yuville.com" className="text-brand-navy font-medium hover:underline">denis@yuville.com</a>
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
